import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { Periods } from "src/app/store/models/right-panel-models";
import { AppService } from "src/app/app.service";
import { initialState } from "../../store/reducers/right-panel-reducer";
import { CommonModalService } from "../common-modal/common-modal.service";
import { Store } from "@ngrx/store";
import { AppState, stateGetter } from "src/app/store/state/app.state";
import { PageParams } from "src/app/store/models/navigation-settings.model";

declare function gridRefresh(eventTarget, event);
declare function OnDefaultSetting(sectionName);

@Component({
  selector: "app-periods",
  templateUrl: "./periods.component.html",
  styleUrls: ["./periods.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class PeriodsComponent implements OnInit {
  actionsDisabled = true;
  tempModel: Periods = { ...initialState.panels.periods };
  originalModel: Periods = { ...initialState.panels.periods };
  yearList = [];
  pageNavParams: PageParams;
  interimsList = [];

  constructor(
    private appService: AppService,
    private dialogService: CommonModalService,
    private store: Store<AppState>,
  ) {}

  ngOnInit() {
    window.addEventListener("MEDataSet", (rpObj: any) => {
      if (
        rpObj.detail.section === "periods" ||
        rpObj.detail.section === "All"
      ) {
        const data = this.getPanelData(rpObj.detail.data.panels);
        if (data !== undefined) {
          this.setInterimsList(data);
          this.setPeriodsData(data);
        }
      }
    });

    this.store
      .select(stateGetter("navigationSettings", "pageParams"))
      .subscribe((pageNavParams: PageParams) => {
        this.pageNavParams = pageNavParams;
      });
  }

  setInterimsList(data): void {
    this.interimsList = data.interims_list;
    if (!this.interimsList.includes("All")) this.interimsList.unshift("All");
  }

  setPeriodsData = (data) => {
    this.originalModel = {
      clearDimension: data.clear_dimension,
      firstEstimatedYear: data.first_estimated_year,
      from: data.from,
      to: data.to,
      interimType: data.interim_type,
      multiPeriod: data.multi_period,
      periodType: data.period_type,
      selectedFrom: data.selected_from.slice(3, 7),
      selectedTo: data.selected_to.slice(3, 7),
    };
    this.tempModel = JSON.parse(JSON.stringify(this.originalModel));
    this.actionsDisabled = true;
    this.generateYearList();

    if (!this.interimsList.includes(this.tempModel.interimType)) {
      this.tempModel.interimType = "All";
    }
  };

  generateYearList = () => {
    const { from, to } = this.originalModel;
    const start = from ? Number(from.slice(3, 7)) : 0;
    const end = to ? Number(to.slice(3, 7)) : -1;
    this.yearList = [];
    for (let i = start; i <= end; i++) {
      this.yearList.push({ year: String(i), label: `FY-${i}` });
    }
  };

  buildYearList = () => {
    const panelIdx = this.getIndexForPanel(window.MEData.panels);
    const { from, to } = window.MEData.panels[panelIdx];
    const firstEstimatedYear =
      window.MEData.panels[panelIdx].first_estimated_year;
    const estStartYear = Number(firstEstimatedYear.match(/\d{4}/));
    const start = from ? Number(from.slice(3, 7)) : 0;
    const end = to ? Number(to.slice(3, 7)) : -1;
    const yearList = [];
    for (let i = start; i <= end; i++) {
      const isForecast = i >= estStartYear;
      yearList.push(`FY-${i}${isForecast ? "E" : ""}`);
    }

    return yearList;
  };

  handleModelChanges = (newPeriod?: string) => {
    const { selectedFrom, selectedTo, multiPeriod } = this.tempModel;
    if (multiPeriod && Number(selectedFrom) > Number(selectedTo)) {
      this.dialogService.create({
        title: null,
        body: "End year should be after Start year",
        mask: false,
        position: { top: 30 },
      });

      this.handleCancel();
      return;
    }
    this.actionsDisabled = this.appService.compare(
      this.tempModel,
      this.originalModel,
    );

    if (newPeriod) {
      const yearList = this.buildYearList();
      const newPeriodStr = yearList.find((yearStr) =>
        yearStr.includes(newPeriod),
      );
      this.logUserAction(newPeriodStr);
    }
  };

  handleCancel = () => {
    const panelIdx = this.getIndexForPanel(window.MEData.panels);
    this.setPeriodsData(window.MEData.panels[panelIdx]);
  };

  handleApply = (evt) => {
    const panelIdx = this.getIndexForPanel(window.MEData.panels);
    //send same periodStart and periodEnd value in gridpanelhistory
    //network call when 'View Single Period' is selected
    // if diff values are passed it works for backend but breaks for addin
    this.tempModel.clearDimension = true;
    if (!this.tempModel.multiPeriod) {
      this.tempModel.selectedTo = this.tempModel.selectedFrom;
    }

    this.updateMEData(this.tempModel);
    this.setPeriodsData(window.MEData.panels[panelIdx]);
    gridRefresh(evt.currentTarget, evt);
  };

  handleClear = (evt: MouseEvent) => {
    //stop collapsing of periods section
    evt.stopImmediatePropagation();
    OnDefaultSetting("periods");
  };

  updateMEData(periodsData: Periods) {
    const firstEstYear = Number(
      periodsData.firstEstimatedYear.match(/\d{4}/)[0],
    );

    const nSelectedFrom = Number(periodsData.selectedFrom.match(/\d{4}/)[0]);
    const nSelectedTo = Number(periodsData.selectedTo.match(/\d{4}/)[0]);
    const selectedFrom = `FY-${nSelectedFrom}${
      nSelectedFrom >= firstEstYear ? "E" : ""
    }`;
    const selectedTo = `FY-${nSelectedTo}${
      nSelectedTo >= firstEstYear ? "E" : ""
    }`;

    const yearList = this.buildYearList();
    const panelIdx = this.getIndexForPanel(window.MEData.panels);
    window.MEData.panels[panelIdx] = {
      ...window.MEData.panels[panelIdx],
      clear_dimension: periodsData.clearDimension,
      first_estimated_year: periodsData.firstEstimatedYear,
      from: periodsData.from,
      to: periodsData.to,
      interim_type: periodsData.interimType,
      multi_period: periodsData.multiPeriod,
      period_type: periodsData.periodType,
      selected_from: selectedFrom,
      selected_to: selectedTo,
      values: yearList.slice(
        yearList.indexOf(selectedFrom),
        yearList.indexOf(selectedTo) + 1,
      ),
    };

    this.setColDimensionsForPeriods();
    // window.rightPanelChanges.panels[0] = window.MEData.panels[0];
  }

  setColDimensionsForPeriods() {
    const panelIdx = this.getIndexForPanel(window.MEData.panels);
    const periodsMEData = window.MEData.panels[panelIdx];
    const firstEstYear = Number(
      periodsMEData.first_estimated_fiscal.match(/\d{4}/)[0],
    );
    const allPeriodsExpansionList = periodsMEData.all_period_expansion_list;

    const periodsDimensions = {
      all_period_expansion_list: [],
      currentquarter: periodsMEData.currentquarter,
      default_display_trend: periodsMEData.default_display_trend,
      first_estimated_fiscal: periodsMEData.first_estimated_fiscal,
      interim_type: periodsMEData.interim_type,
      name: periodsMEData.name,
      type: periodsMEData.type,
      expansions: [],
      values: [],
    };

    periodsMEData.values.forEach((periodFye) => {
      const detail = allPeriodsExpansionList.find(
        (period) => period.year === Number(periodFye.match(/\d{4}/)[0]),
      );

      if (periodsMEData.period_type === "AI") {
        //objects in values array will be Annuals
        //they will include expansion_lst containing names of interims

        const displayObject = {
          display_period: detail.display_period,
          period: detail.period,
          forecast: detail.forecast,
          expanded: detail.expanded,
          expansion_lst: detail.expansion_lst,
          year: detail.year,
        };

        periodsDimensions.values.push(displayObject);
      } else if (periodsMEData.period_type === "A") {
        //objects in values array will be Annuals
        //they will include empty expansion_lst
        const displayObject = {
          display_period: detail.display_period,
          period: detail.period,
          forecast: detail.forecast,
          expanded: detail.expanded,
          expansion_lst: [],
          year: detail.year,
        };

        periodsDimensions.values.push(displayObject);
      } else if (periodsMEData.period_type === "I") {
        //objects in values array will include Interims
        //they will include empty expansion_lst

        detail.expansion_lst.forEach((interim) => {
          const interimKey = Object.keys(interim)[0];
          const isInterimForecast = periodsMEData.actual_estimates[interimKey];

          let forecast = true;
          if (detail.year < firstEstYear) {
            forecast = false;
          } else if (detail.year > firstEstYear) {
            forecast = true;
          } else if (detail.year === firstEstYear) {
            if (isInterimForecast !== undefined) {
              forecast = isInterimForecast;
            } else {
              forecast = true;
            }
          }

          const isMultiPeriod = periodsMEData.multi_period;
          const interimType = periodsMEData.interim_type;
          const shouldIncludeQuarter =
            interimType === "All" || interimKey.includes(interimType);

          if (!isMultiPeriod && !shouldIncludeQuarter) {
            return;
          }

          const displayObject = {
            forecast: forecast,
            display_period: `${interimKey.split("-")[0]}${detail.year % 100}${
              forecast ? "E" : ""
            }`,
            period: interimKey,
            expansion_lst: [],
            expanded: false,
            year: detail.year,
          };

          periodsDimensions.values.push(displayObject);
        });
      }
    });

    const colDimensions = window.MEData.column_dimensions;
    const periodsColIdx = colDimensions.findIndex(
      (dimension) => dimension.name === "periods",
    );
    if (periodsColIdx > -1) {
      window.MEData.column_dimensions.splice(periodsColIdx, 1);
    }
    window.MEData.column_dimensions.unshift(periodsDimensions);
  }

  getPanelData(panelsList) {
    return panelsList.find((panel) => panel.name.toLowerCase() === "periods");
  }

  getIndexForPanel(panelsList) {
    return panelsList.findIndex(
      (panel) => panel.name.toLowerCase() === "periods",
    );
  }

  logUserAction(period: string) {
    this.appService.logUserAction(
      {},
      this.pageNavParams.ticker,
      this.pageNavParams.cut,
      "OPTIONS_PERIODS_END",
      {
        PERIOD_VALUE: period,
        URL: window.location.toString(),
        UTCTIME: new Date().toUTCString(),
      },
      "" || this.pageNavParams.source,
      null,
    );
  }
}
